import React from 'react';
import { ErrorMessage, Field } from 'formik';
import { produce } from 'immer';
import { Divider, FormControl, FormLabel } from '@material-ui/core';
import { get } from 'lodash';
import { RETAINER_ARCH_OPTIONS, RETAINER_ATTRIBUTES, RETAINER_QUESTIONS } from './constants';
import { ASSIGNMENTS, SCAN_SUBMISSION_METHODS } from '../IDB/constants';
import { autoDetectStlTypes } from '../IDB/helpers';
import ScanSubmissionMethodField from '../IDB/Fields/ScanSubmissionMethodField';
import ScannerField from '../IDB/Fields/ScannerField';
import DoctorSelectionField from '../Fields/DoctorSelectionField';
import LocationField from '../Fields/LocationField';
import RadioGroupField from '../Fields/RadioGroupField';
import ShippingMethodField from '../Fields/ShippingMethodField';
import TextField from '../Fields/Formik/TextField';
import NameField from '../../Formik/NameField';
import SanitizeForbiddenCharactersField from '../Fields/Formik/SanitizeForbiddenCharactersField';
import RetainerCutTypeField from './Fields/RetainerCutTypeField';
import UploadRxFormFile from '../../../pages/RxForms/components/UploadRxFormFile';
import InputErrorMessage from '../../InputErrorMessage';
import { ACCEPTED_FILE_TYPES, FILE_TYPE_IDS, ZIP_EXTRACTABLE_FILE_TYPES } from '../../../constants';
import tags from '../../../constants/tags';

const createRetainerNativeFields = ({
  withDoctorSelection,
  doctors,
  preference,
  isSubmitting,
  availableLocations,
  pricing,
  decodedValues,
  values,
  setFieldValue,
  setFieldTouched,
  organization,
  GridField
}) => {
  return {
    [RETAINER_ATTRIBUTES.FIRST_NAME]: (
      <GridField
        xs={12}
        sm={6}
        key={RETAINER_ATTRIBUTES.FIRST_NAME}
        name={RETAINER_ATTRIBUTES.FIRST_NAME}
      >
        <Field
          variant="outlined"
          fullWidth
          required
          id="first_name"
          label="Patient First Name"
          name="first_name"
          component={SanitizeForbiddenCharactersField}
          originalComponent={NameField}
        />
      </GridField>
    ),
    [RETAINER_ATTRIBUTES.LAST_NAME]: (
      <GridField
        key={RETAINER_ATTRIBUTES.LAST_NAME}
        name={RETAINER_ATTRIBUTES.LAST_NAME}
        xs={12}
        sm={6}
      >
        <Field
          name="last_name"
          variant="outlined"
          required
          fullWidth
          id="last_name"
          label="Patient Last Name"
          component={SanitizeForbiddenCharactersField}
          originalComponent={NameField}
        />
      </GridField>
    ),
    [RETAINER_ATTRIBUTES.CASE_NUMBER_TEXT]: (
      <GridField
        key={RETAINER_ATTRIBUTES.CASE_NUMBER_TEXT}
        name={RETAINER_ATTRIBUTES.CASE_NUMBER_TEXT}
        xs={12}
        sm={6}
      >
        <Field
          name="case_number_text"
          variant="outlined"
          fullWidth
          id="case_number_text"
          label="Patient Case Number"
          component={SanitizeForbiddenCharactersField}
          originalComponent={TextField}
        />
      </GridField>
    ),
    [RETAINER_ATTRIBUTES.DOCTOR]: withDoctorSelection && doctors.list.length > 1 && (
      <GridField key={RETAINER_ATTRIBUTES.DOCTOR} name={RETAINER_ATTRIBUTES.DOCTOR} xs={12} md={6}>
        <DoctorSelectionField
          name={RETAINER_ATTRIBUTES.DOCTOR}
          doctors={doctors.list}
          fetching={doctors.fetching}
          preferenceValue={preference[RETAINER_ATTRIBUTES.DOCTOR]}
          isSubmitting={isSubmitting}
        />
      </GridField>
    ),
    [RETAINER_ATTRIBUTES.ARCH_TYPE]: (
      <GridField key={RETAINER_ATTRIBUTES.ARCH_TYPE} name={RETAINER_ATTRIBUTES.ARCH_TYPE} xs={12}>
        <RadioGroupField
          name={RETAINER_ATTRIBUTES.ARCH_TYPE}
          options={RETAINER_ARCH_OPTIONS}
          overrideLabel={RETAINER_QUESTIONS[RETAINER_ATTRIBUTES.ARCH_TYPE]}
          isSubmitting={isSubmitting}
        />
      </GridField>
    ),
    [RETAINER_ATTRIBUTES.RETAINER_CUT_TYPE]: (
      <GridField
        key={RETAINER_ATTRIBUTES.RETAINER_CUT_TYPE}
        name={RETAINER_ATTRIBUTES.RETAINER_CUT_TYPE}
        xs={12}
        sm={6}
      >
        <RetainerCutTypeField
          name={RETAINER_ATTRIBUTES.RETAINER_CUT_TYPE}
          fees={get(pricing, 'retainer', {})}
        />
      </GridField>
    ),
    [RETAINER_ATTRIBUTES.SCAN_SUBMISSION_METHOD]: (
      <>
        <GridField
          key={RETAINER_ATTRIBUTES.SCAN_SUBMISSION_METHOD}
          name={RETAINER_ATTRIBUTES.SCAN_SUBMISSION_METHOD}
          xs={12}
        >
          <>
            <FormControl component="fieldset">
              <ScanSubmissionMethodField
                assignment={ASSIGNMENTS.ORTHOSELECT}
                preferenceValue={preference.scan_submission_options}
                isSubmitting={isSubmitting}
              />
            </FormControl>
            <Divider />
          </>
        </GridField>

        <GridField
          name={RETAINER_ATTRIBUTES.SCAN_SUBMISSION_METHOD}
          xs={12}
          show={decodedValues.scan_submission_type === SCAN_SUBMISSION_METHODS.UPLOAD}
        >
          <>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Upload intraoral STL files *</FormLabel>
              <UploadRxFormFile
                accept={ACCEPTED_FILE_TYPES[FILE_TYPE_IDS.stl]}
                dependency={decodedValues.scan_submission_type === SCAN_SUBMISSION_METHODS.UPLOAD}
                name={RETAINER_ATTRIBUTES.STL_FILES}
                preferenceValue={[]}
                title="Upload STL Files"
                onChange={(updatedFiles, filePath) => {
                  let files = autoDetectStlTypes(updatedFiles || []);

                  if (typeof filePath !== 'undefined') {
                    files = produce(files, draft => {
                      const index = draft.findIndex(file => file.file_path === filePath);

                      if (index !== -1) {
                        const updatedFile = draft[index];
                        // set other type to opposite
                        const otherIndex = draft.findIndex(
                          file => file.file_path !== filePath && file.tag
                        );

                        if (otherIndex !== -1 && draft[otherIndex].tag === updatedFile.tag) {
                          draft[otherIndex].tag =
                            updatedFile.tag === tags.STL_MANDIBULAR
                              ? tags.STL_MAXILLARY
                              : tags.STL_MANDIBULAR;
                        }
                      }
                    });
                  }

                  setTimeout(() => setFieldValue('stl_files', files));
                  setFieldTouched('stl_files', true, false);
                }}
                values={values.stl_files}
                label="Add Files"
                maxFiles={10}
                organization={organization}
                autoExtractCompressedFiles={ZIP_EXTRACTABLE_FILE_TYPES[FILE_TYPE_IDS.stl] || null}
                existingFiles={values.stl_files}
                preventDuplicate
              />
              <ErrorMessage
                name="stl_files"
                render={message => <InputErrorMessage>{message}</InputErrorMessage>}
              />
            </FormControl>
            <Divider />
          </>
        </GridField>

        <GridField
          name={RETAINER_ATTRIBUTES.SCANNER}
          xs={12}
          skipOnly
          show={
            decodedValues[RETAINER_ATTRIBUTES.SCAN_SUBMISSION_METHOD] ===
            SCAN_SUBMISSION_METHODS.SCANNER
          }
        >
          <>
            <ScannerField
              name={RETAINER_ATTRIBUTES.SCANNER}
              dependency={
                decodedValues[RETAINER_ATTRIBUTES.SCAN_SUBMISSION_METHOD] ===
                SCAN_SUBMISSION_METHODS.SCANNER
              }
              isSubmitting={isSubmitting}
            />
            <Divider />
          </>
        </GridField>
      </>
    ),
    [RETAINER_ATTRIBUTES.INTRAORAL_PHOTOS]: (
      <GridField
        key={RETAINER_ATTRIBUTES.INTRAORAL_PHOTOS}
        name={RETAINER_ATTRIBUTES.INTRAORAL_PHOTOS}
        xs={12}
      >
        <>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              Upload intraoral photos here (we require at least one intraoral to verify the scans
              match the patient) *
            </FormLabel>
            <UploadRxFormFile
              accept={ACCEPTED_FILE_TYPES[FILE_TYPE_IDS.photos]}
              name={RETAINER_ATTRIBUTES.INTRAORAL_PHOTOS}
              title="Upload Intraoral Photos"
              onChange={files => {
                setTimeout(() => setFieldValue('rx_intraoral_photos', files));
                setFieldTouched('rx_intraoral_photos', true, false);
              }}
              values={values.rx_intraoral_photos}
              label="Add Files"
              maxFiles={10}
              organization={organization}
              preferenceValue={[]}
              autoExtractCompressedFiles={ZIP_EXTRACTABLE_FILE_TYPES[FILE_TYPE_IDS.photos] || null}
              existingFiles={values.rx_intraoral_photos}
              preventDuplicate
            />
            <ErrorMessage
              name="rx_intraoral_photos"
              render={message => <InputErrorMessage>{message}</InputErrorMessage>}
            />
          </FormControl>
          <Divider />
        </>
      </GridField>
    ),
    [RETAINER_ATTRIBUTES.SHIPPING_LOCATION]: (
      <GridField
        key={RETAINER_ATTRIBUTES.SHIPPING_LOCATION}
        name={RETAINER_ATTRIBUTES.SHIPPING_LOCATION}
        xs={12}
        md={6}
      >
        <LocationField
          name={RETAINER_ATTRIBUTES.SHIPPING_LOCATION}
          locations={availableLocations}
          dependency
          preferenceValue={preference[RETAINER_ATTRIBUTES.SHIPPING_LOCATION]}
          isSubmitting={isSubmitting}
          required
        />
      </GridField>
    ),
    [RETAINER_ATTRIBUTES.SHIPPING_METHOD]: (
      <GridField
        key={RETAINER_ATTRIBUTES.SHIPPING_METHOD}
        name={RETAINER_ATTRIBUTES.SHIPPING_METHOD}
        xs={12}
        md={6}
      >
        <ShippingMethodField
          name={RETAINER_ATTRIBUTES.SHIPPING_METHOD}
          dependency
          assignment={ASSIGNMENTS.ORTHOSELECT}
          preferenceValue={preference[RETAINER_ATTRIBUTES.SHIPPING_METHOD]}
          isSubmitting={isSubmitting}
        />
      </GridField>
    ),
    [RETAINER_ATTRIBUTES.NOTES]: (
      <GridField key={RETAINER_ATTRIBUTES.NOTES} name={RETAINER_ATTRIBUTES.NOTES} xs={12}>
        <Field
          variant="outlined"
          fullWidth
          id="special_instructions"
          label={`(Optional) ${RETAINER_QUESTIONS[RETAINER_ATTRIBUTES.NOTES]}`}
          name="special_instructions"
          multiline
          component={TextField}
        />
      </GridField>
    )
  };
};

export default createRetainerNativeFields;
